import { Link } from 'react-router-dom'
import { WA_NUMBER, WA_MESSAGES } from '../../constants'

// Hero for a trek page: title + key facts on the left, price + CTAs on the right.
// Everything is read from the trek object, so this stays reusable across treks.
export default function TrekHeroBanner({ trek }) {
  const waMessage = WA_MESSAGES[trek.waKey] || WA_MESSAGES.general
  const waHref = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(waMessage)}`

  // Only render facts we actually have.
  const facts = [
    trek.quickFacts?.region?.value && !String(trek.quickFacts.region.value).startsWith('TODO')
      ? { icon: '📍', label: trek.quickFacts.region.value }
      : { icon: '📍', label: 'Nepal' },
    trek.duration ? { icon: '🗓️', label: `${trek.duration} Days` } : null,
    trek.difficulty ? { icon: '⛰️', label: trek.difficulty } : null,
    trek.maxAltitude ? { icon: '📈', label: `${trek.maxAltitude.toLocaleString()}m max` } : null,
  ].filter(Boolean)

  // Real photo when the trek has one; brand gradient otherwise.
  // The dark overlay keeps the white text readable over any image.
  // Darkens the bottom, where the text sits, and fades out towards the top so
  // the peak in the photo stays visible.
  // NOTE: backgroundImage (not the `background` shorthand) — the shorthand would
  // reset background-position inline and override the responsive CSS below.
  // Near-black INK scrim (from --color-text #1A1C1B → rgb 26,28,27), so the hero
  // photo reads as documentary rather than green-tinted. Fallback = flat near-black.
  const backgroundImage = trek.heroImage
    ? `linear-gradient(to top, rgba(26,28,27,0.92) 0%, rgba(26,28,27,0.6) 40%, rgba(26,28,27,0.22) 75%, rgba(26,28,27,0.12) 100%), url(${process.env.PUBLIC_URL}${trek.heroImage})`
    : 'var(--color-text)'

  return (
    <section className="trek-hero" style={{
      backgroundImage,
      backgroundSize: 'cover',
      color: 'white',
      padding: '140px 5% 64px',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'flex-end',
    }}>
      {/* Mountain silhouette decoration — only when there's no real photo */}
      {!trek.heroImage && (
        <svg viewBox="0 0 400 160" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, width: '100%', opacity: 0.18 }}>
          <polygon points="0,160 80,60 160,110 240,40 320,90 400,30 400,160" fill="white" />
        </svg>
      )}

      <div className="trek-hero-grid" style={{
        position: 'relative', zIndex: 1,
        width: '100%',
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        gap: '48px',
        alignItems: 'end',
      }}>
        {/* Left: title, facts, blurb */}
        <div style={{ maxWidth: '680px' }}>
          <div style={{ fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.85)', fontWeight: 600, marginBottom: '14px' }}>
            {trek.tagline}
          </div>

          <h1 className="serif" style={{ fontSize: 'clamp(32px, 5.5vw, 54px)', fontWeight: 600, lineHeight: 1.1, marginBottom: '18px' }}>
            {trek.name}
          </h1>

          <div style={{ display: 'flex', gap: '22px', flexWrap: 'wrap', marginBottom: '18px' }}>
            {facts.map(f => (
              <span key={f.label} style={{
                display: 'flex', alignItems: 'center', gap: '7px',
                fontSize: '13px', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase',
              }}>
                <span aria-hidden="true">{f.icon}</span>{f.label}
              </span>
            ))}
          </div>

          <p style={{ fontSize: '15px', lineHeight: 1.65, color: 'rgba(255,255,255,0.82)', fontWeight: 300 }}>
            {trek.description}
          </p>
        </div>

        {/* Right: price + CTAs */}
        <div className="trek-hero-price" style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)', marginBottom: '2px' }}>From</div>
          <div style={{ fontSize: 'clamp(30px, 4vw, 42px)', fontWeight: 600, lineHeight: 1.1, marginBottom: '4px' }}>
            {trek.priceFrom ? `US$ ${trek.priceFrom.toLocaleString()}` : 'Price on request'}
          </div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginBottom: '20px' }}>
            {trek.priceFrom ? 'per person' : 'message us for a quote'}{trek.groupSize ? ` · small groups of ${trek.groupSize}` : ''}
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <a href={waHref} target="_blank" rel="noreferrer" className="btn btn-green">
              WhatsApp to Book
            </a>
            <Link
              to="/departures"
              className="btn"
              style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.35)', color: 'white' }}
            >
              Join a Group
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        /* The summit sits upper-left of centre in the photo. Wide screens crop
           the image vertically, narrow screens crop it horizontally — so bias
           the focal point per breakpoint to keep the peak in frame. */
        .trek-hero {
          min-height: 78vh;
          background-position: 32% 8%;
        }
        @media (max-width: 860px) {
          .trek-hero { min-height: 68vh; background-position: 15% 0%; }
          .trek-hero-grid { grid-template-columns: 1fr !important; gap: 28px !important; }
          .trek-hero-price { text-align: left !important; }
          .trek-hero-price > div:last-child { justify-content: flex-start !important; }
        }
      `}</style>
    </section>
  )
}
